<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseBubble from './Base.vue';
import { useMessageContext } from '../provider.js';
import { buildFlowResponseEntries } from '../helpers/whatsappFlowResponse.js';
// NOTICE: KAPTION-INTEGRATION - whatsapp-flows: labels from the Flow JSON,
// verified badge, media thumbnails, sensitive-field masking (non-admins) and
// a raw JSON toggle, all from content_attributes.kaption_flow. Without it the
// bubble renders exactly like upstream #15279.
import { useAdmin } from 'dashboard/composables/useAdmin';
import {
  buildLabeledEntries,
  maskSensitiveAnswers,
} from 'dashboard/helper/kaptionWhatsappFlows';

const { content, contentAttributes, attachments, inboxId } =
  useMessageContext();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const showRawJson = ref(false);

const responseJson = computed(
  () => contentAttributes.value?.whatsappFlowResponse?.responseJson ?? {}
);
const kaptionFlow = computed(() => contentAttributes.value?.kaptionFlow);

const responseEntries = computed(() => {
  if (!kaptionFlow.value) return buildFlowResponseEntries(responseJson.value);
  return buildLabeledEntries(responseJson.value, kaptionFlow.value, {
    showSensitive: isAdmin.value,
    booleanLabels: {
      yes: t('KAPTION_WHATSAPP_FLOWS.VALUES.YES'),
      no: t('KAPTION_WHATSAPP_FLOWS.VALUES.NO'),
    },
  });
});

const media = computed(() => {
  const ids = (kaptionFlow.value?.media || []).map(String);
  if (!ids.length) return [];
  return (attachments?.value || []).filter(a => ids.includes(String(a.id)));
});

// The correlation token is internal (kf1...); never shown, even raw.
const rawJson = computed(() => {
  const { flow_token: _token, ...answers } =
    responseJson.value && typeof responseJson.value === 'object'
      ? responseJson.value
      : {};
  return JSON.stringify(
    isAdmin.value ? answers : maskSensitiveAnswers(answers, kaptionFlow.value),
    null,
    2
  );
});

const allResponsesRoute = computed(() => {
  const flowId = kaptionFlow.value?.flowId;
  if (!flowId || !inboxId?.value) return null;
  return {
    name: 'kaption_whatsapp_flow_responses',
    params: { inboxId: inboxId.value, flowId },
  };
});
</script>

<template>
  <BaseBubble
    class="px-4 py-3 min-w-64 max-w-lg"
    data-bubble-name="whatsapp-flow-response"
  >
    <div v-if="kaptionFlow" class="flex flex-wrap items-center gap-2 mb-1">
      <span class="i-lucide-clipboard-check size-4 text-n-teal-10" />
      <span class="text-xs font-medium text-n-slate-11">
        {{ t('KAPTION_WHATSAPP_FLOWS.BUBBLE.COMPLETED') }}
        <template v-if="kaptionFlow.flowName">
          {{ `· ${kaptionFlow.flowName}` }}
        </template>
      </span>
      <span
        class="px-1.5 py-0.5 rounded text-[11px] font-medium"
        :class="
          kaptionFlow.verified
            ? 'bg-n-teal-3 text-n-teal-11'
            : 'bg-n-amber-3 text-n-amber-11'
        "
        :title="
          kaptionFlow.verified
            ? ''
            : t('KAPTION_WHATSAPP_FLOWS.BUBBLE.UNVERIFIED_HINT')
        "
        data-testid="kaption-flow-verified"
      >
        {{
          kaptionFlow.verified
            ? t('KAPTION_WHATSAPP_FLOWS.BUBBLE.VERIFIED')
            : t('KAPTION_WHATSAPP_FLOWS.BUBBLE.UNVERIFIED')
        }}
      </span>
    </div>
    <div class="font-medium">
      {{ content || t('CONVERSATION.WHATSAPP_FLOW_RESPONSE') }}
    </div>
    <dl v-if="responseEntries.length" class="mt-3 space-y-3">
      <div v-for="entry in responseEntries" :key="entry.key">
        <dt class="text-xs text-n-slate-11">
          {{ entry.label }}
          <span
            v-if="entry.sensitive"
            class="i-lucide-lock size-3 align-[-1px]"
          />
        </dt>
        <dd class="mt-0.5 whitespace-pre-wrap break-words">
          {{ entry.value }}
        </dd>
      </div>
    </dl>
    <div v-if="media.length" class="mt-3" data-testid="kaption-flow-media">
      <div class="text-xs text-n-slate-11">
        {{ t('KAPTION_WHATSAPP_FLOWS.BUBBLE.MEDIA') }}
      </div>
      <div class="flex flex-wrap gap-2 mt-1">
        <a
          v-for="item in media"
          :key="item.id"
          :href="item.dataUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="block overflow-hidden border rounded-lg size-16 border-n-weak"
        >
          <img
            v-if="item.fileType === 'image'"
            :src="item.thumbUrl || item.dataUrl"
            class="object-cover size-full"
            alt=""
          />
          <span
            v-else
            class="flex items-center justify-center size-full i-lucide-file"
          />
        </a>
      </div>
    </div>
    <div
      v-if="kaptionFlow"
      class="flex flex-wrap items-center gap-3 mt-3 text-xs"
    >
      <button
        type="button"
        class="text-n-slate-11 hover:text-n-slate-12"
        data-testid="kaption-flow-raw-toggle"
        @click="showRawJson = !showRawJson"
      >
        {{
          showRawJson
            ? t('KAPTION_WHATSAPP_FLOWS.BUBBLE.HIDE_RAW_JSON')
            : t('KAPTION_WHATSAPP_FLOWS.BUBBLE.RAW_JSON')
        }}
      </button>
      <router-link
        v-if="allResponsesRoute"
        :to="allResponsesRoute"
        class="text-n-brand"
      >
        {{ t('KAPTION_WHATSAPP_FLOWS.BUBBLE.ALL_RESPONSES') }}
      </router-link>
    </div>
    <pre
      v-if="showRawJson"
      class="p-2 mt-2 text-xs rounded-lg whitespace-pre-wrap break-all bg-n-alpha-black2"
      data-testid="kaption-flow-raw-json"
      >{{ rawJson }}</pre
    >
  </BaseBubble>
</template>
